import { takeEvery, call, put } from "redux-saga/effects";
import {
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchUserProfileStart,
  fetchUserProfileSuccess,
  fetchUserProfileFail,
  createUserStart,
  createUserSuccess,
  createUserFail,
  updateUserStart,
  updateUserSuccess,
  updateUserFail,
  submitAnswersStart,
  submitAnswersSuccess,
  submitAnswersFail,
  fetchUserAnswersStart,
  fetchUserAnswersSuccess,
  fetchUserAnswersFail,
  fetchUserProgressStart,
  fetchUserProgressSuccess,
  fetchUserProgressFail,
  fetchUserResultsStart,
  fetchUserResultsSuccess,
  fetchUserResultsFail,
  fetchDeletedUsersStart,
  fetchDeletedUsersSuccess,
  fetchDeletedUsersFail,
} from "../slices/userSlice";
import { getRequest, postRequest, putRequest } from "../../network";
import {
  createUser,
  getUsers,
  getDeletedUsers,
  getUserDetails,
  updateUser,
  submitAnswer,
  getUserAnswers,
  getUserProgress,
  getUserResults,
} from "../apis";

function* userSaga(action) {
  const {
    endpoint,
    method = "get",
    successAction,
    failAction,
    payload = null,
    callback = null,
  } = action.payload;
  const requestFunction =
    method === "post"
      ? postRequest
      : method === "put"
        ? putRequest
        : getRequest;
  try {
    const response = yield call(requestFunction, endpoint, payload);
    if (callback && typeof callback === "function") {
      yield call(callback, true);
    }
    yield put(successAction(response));
  } catch (error) {
    if (callback && typeof callback === "function") {
      let errorMessage = "Something went wrong";
      if (error.response && error.response.data) {
        const data = error.response.data;
        if (data.message && data.errors) {
          errorMessage = `${data.message}: ${data.errors.join(', ')}`;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      yield call(callback, errorMessage);
    }
    yield put(failAction(error.message));
  }
}

function* fetchUsersSaga(action) {
  yield userSaga({
    payload: {
      endpoint: getUsers,
      successAction: fetchUsersSuccess,
      failAction: fetchUsersFail,
      payload: action.payload,
    },
  });
}

// Fetch Single User Profile Saga
function* fetchUserProfileSaga(action) {
  yield userSaga({
    payload: {
      endpoint: getUserDetails.replace(":id", action.payload.userID),
      successAction: fetchUserProfileSuccess,
      failAction: fetchUserProfileFail,
    },
  });
}

// Create User Saga
function* createUserSaga(action) {
  yield userSaga({
    payload: {
      endpoint: createUser,
      method: "post",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: createUserSuccess,
      failAction: createUserFail,
    },
  });
}

// Update User Saga
function* updateUserSaga(action) {
  yield userSaga({
    payload: {
      endpoint: updateUser.replace(":id", action.payload.id),
      method: "put",
      payload: action.payload.data,
      callback: action.payload.callback,
      successAction: updateUserSuccess,
      failAction: updateUserFail,
    },
  });
}

// Submit Answers Saga
function* submitAnswersSaga(action) {
  yield userSaga({
    payload: {
      endpoint: submitAnswer,
      method: "post",
      payload: action.payload,
      successAction: submitAnswersSuccess,
      failAction: submitAnswersFail,
    },
  });
}

// Fetch User Answers Saga
function* fetchUserAnswersSaga(action) {
  yield userSaga({
    payload: {
      endpoint: getUserAnswers,
      payload: action.payload,
      successAction: fetchUserAnswersSuccess,
      failAction: fetchUserAnswersFail,
    },
  });
}

// Fetch User Progress Saga
function* fetchUserProgressSaga(action) {
  yield userSaga({
    payload: {
      endpoint: getUserProgress,
      payload: action.payload,
      successAction: fetchUserProgressSuccess,
      failAction: fetchUserProgressFail,
    },
  });
}

// Fetch User Results Saga
function* fetchUserResultsSaga(action) {
  yield userSaga({
    payload: {
      endpoint: getUserResults,
      payload: action.payload,
      successAction: fetchUserResultsSuccess,
      failAction: fetchUserResultsFail,
    },
  });
}

// Fetch Deleted Users Saga
function* fetchDeletedUsersSaga(action) {
  yield userSaga({
    payload: {
      endpoint: getDeletedUsers,
      payload: action.payload,
      successAction: fetchDeletedUsersSuccess,
      failAction: fetchDeletedUsersFail,
    },
  });
}

// Watcher Saga
export function* watchUsers() {
  yield takeEvery(fetchUsersStart.type, fetchUsersSaga);
  yield takeEvery(fetchUserProfileStart.type, fetchUserProfileSaga);
  yield takeEvery(createUserStart.type, createUserSaga);
  yield takeEvery(updateUserStart.type, updateUserSaga);
  yield takeEvery(submitAnswersStart.type, submitAnswersSaga);
  yield takeEvery(fetchUserAnswersStart.type, fetchUserAnswersSaga);
  yield takeEvery(fetchUserProgressStart.type, fetchUserProgressSaga);
  yield takeEvery(fetchUserResultsStart.type, fetchUserResultsSaga);
  yield takeEvery(fetchDeletedUsersStart.type, fetchDeletedUsersSaga);
}
